import { useEffect } from "react";

/**
 * Mobile virtual keyboard awareness.
 *
 * - Tracks `visualViewport` and sets `--kb-inset` on <html> with the keyboard height.
 * - When an input/textarea/contenteditable gains focus and the keyboard is up,
 *   scrolls that element into a comfortable position above the keyboard.
 * - Cheap: single listener attached at app root.
 *
 * Silent no-op on browsers without `visualViewport` (very old Android WebView).
 */
export function useMobileKeyboard(): void {
  useEffect(() => {
    if (typeof window === "undefined") return;
    const vv = window.visualViewport;
    const root = document.documentElement;
    if (!vv) return;

    let activeEl: HTMLElement | null = null;

    const isEditable = (el: EventTarget | null): el is HTMLElement => {
      if (!(el instanceof HTMLElement)) return false;
      const tag = el.tagName;
      return (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        tag === "SELECT" ||
        el.isContentEditable
      );
    };

    const compute = () => {
      // Difference between layout viewport height and visual viewport height ≈ keyboard.
      const kb = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      root.style.setProperty("--kb-inset", `${Math.round(kb)}px`);
      root.dataset.kbOpen = kb > 80 ? "1" : "0";

      // If keyboard just opened, bring focused element into view.
      if (kb > 80 && activeEl) {
        try {
          activeEl.scrollIntoView({ block: "center", behavior: "smooth" });
        } catch {
          /* no-op */
        }
      }
    };

    const onFocusIn = (e: FocusEvent) => {
      if (!isEditable(e.target)) return;
      activeEl = e.target as HTMLElement;
      // Give the keyboard a moment to appear, then recompute.
      window.setTimeout(compute, 120);
      window.setTimeout(compute, 320);
    };
    const onFocusOut = () => {
      activeEl = null;
    };

    vv.addEventListener("resize", compute);
    vv.addEventListener("scroll", compute);
    window.addEventListener("focusin", onFocusIn);
    window.addEventListener("focusout", onFocusOut);
    compute();

    return () => {
      vv.removeEventListener("resize", compute);
      vv.removeEventListener("scroll", compute);
      window.removeEventListener("focusin", onFocusIn);
      window.removeEventListener("focusout", onFocusOut);
      root.style.removeProperty("--kb-inset");
      delete root.dataset.kbOpen;
    };
  }, []);
}
